import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import PageLoader from "./PageLoader";
import { adminApiRequest } from "../utils/adminApi";

export default function TableBookings({ setIsAnyModalActive }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalConfig, setModalConfig] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchBookings = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);

    try {
      const data = await adminApiRequest({
        script: "get-table-bookings",
      });

      if (data?.success || data?.status === "success") {
        const rawBookings = data.bookings || [];
        const bookingsArray = Array.isArray(rawBookings)
          ? rawBookings
          : Object.values(rawBookings);
        setBookings(bookingsArray);
      }
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings(true);
  }, []);

  const handleAction = async (bookingId, action) => {
    setActionLoading(true);
    try {
      const data = await adminApiRequest({
        script: "delete-table-booking",
        data: { id: bookingId },
      });

      if (data?.success || data?.status === "success") {
        setModalConfig(null);
        fetchBookings(false);
      } else {
        alert(data.message || "Failed to update booking status");
      }
    } catch (err) {
      console.error("Action error:", err);
      alert("Error connecting to server");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <PageLoader message="Fetching Reservations..." />;
  }

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Table Bookings</h1>
          <p className="text-sm text-slate-500">
            Real-time table reservations management
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="h-full overflow-auto sidebar-scrollbar">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-slate-50">
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-6 py-4 font-bold">S/N</th>
                <th className="px-6 py-4 font-bold">First Name</th>
                <th className="px-6 py-4 font-bold">Last Name</th>
                <th className="px-6 py-4 font-bold">Phone</th>
                <th className="px-6 py-4 font-bold">Email</th>
                <th className="px-6 py-4 font-bold text-center">Seats</th>
                <th className="px-6 py-4 font-bold">Date & Time</th>
                <th className="px-6 py-4 font-bold">Special Request</th>
                <th className="px-6 py-4 text-center font-bold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {bookings.length > 0 ? (
                bookings.map((booking, index) => (
                  <tr
                    key={booking.id}
                    className="group transition hover:bg-slate-50/50"
                  >
                    <td className="px-6 py-4 text-slate-400">{index + 1}</td>
                    <td className="px-6 py-4 font-medium text-slate-900">
                      {booking.first_name || booking.customer_name?.split(" ")[0] || "—"}
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900">
                      {booking.last_name || booking.customer_name?.split(" ").slice(1).join(" ") || "—"}
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {booking.phone || booking.customer_phone || "—"}
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {booking.email || booking.customer_email || "—"}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="rounded-lg bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-600">
                        {booking.seats || booking.number_of_people || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600 whitespace-nowrap">
                      <div className="font-medium">{booking.booking_date}</div>
                      <div className="text-xs text-slate-500">{booking.booking_time}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-500 italic max-w-[200px] truncate" title={booking.special_request || booking.special_requests || "None"}>
                      {booking.special_request || booking.special_requests || "None"}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button title="Delete"
                        onClick={() =>
                          setModalConfig({
                            id: booking.id,
                            action: "delete",
                            title: "Delete Booking?",
                          })
                        }
                        className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600 transition hover:bg-red-500 hover:text-white mx-auto shadow-sm"
                      >
                        <i className="fa-solid fa-trash-can text-sm"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="px-6 py-12 text-center text-slate-400"
                  >
                    No bookings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalConfig && (
        <Modal
          title={modalConfig.title}
          onClose={() => setModalConfig(null)}
          onSubmit={() => handleAction(modalConfig.id, modalConfig.action)}
          submitButtonText={actionLoading ? "Processing..." : "Confirm"}
          disableSubmit={actionLoading}
          showCancel={!actionLoading}
          showSubmitButton={true}
          submitButtonClass="bg-red-600 hover:bg-red-700 text-white"
        >
          <p className="text-sm text-slate-600">
            Are you sure you want to {modalConfig.action} this reservation?
            This action cannot be undone.
          </p>
        </Modal>
      )}
    </div>
  );
}
